import type { Components, Theme } from "@mui/material/styles";
import { BODY_FONT, C, DISPLAY_FONT, R } from "../colors";

// House style for every MUI component, so pages that still use raw MUI pick up
// the Inflos look without changes: ink controls, hairline borders, flat cards,
// small-caps table heads, 5px status chips, underlined tabs.
export const componentsTheme: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      "html, body, #root": { height: "100%" },
      body: {
        margin: 0,
        backgroundColor: C.ground,
        color: C.ink,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        overflowX: "hidden",
      },
      "*::-webkit-scrollbar": { width: 10, height: 10 },
      "*::-webkit-scrollbar-thumb": { background: C.line2, borderRadius: 8, border: `2px solid ${C.ground}` },
      "*::-webkit-scrollbar-track": { background: "transparent" },
      ".num, .mono": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.02em" },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": { animationDuration: "0.01ms !important", transitionDuration: "0.01ms !important" },
      },
    },
  },
  MuiButtonBase: { defaultProps: { disableRipple: true } },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: R.ctl,
        fontWeight: 700,
        minHeight: 38,
        padding: "6px 14px",
        textTransform: "none",
        boxShadow: "none",
        whiteSpace: "nowrap",
        "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 2 },
      },
      sizeSmall: { minHeight: 32, padding: "4px 10px", fontSize: "0.8125rem" },
      sizeLarge: { minHeight: 46, padding: "8px 20px", fontSize: "0.9375rem" },
      containedPrimary: { "&:hover": { backgroundColor: "#2A2A2A" } },
      outlined: { borderColor: C.line2, color: C.ink, backgroundColor: C.paper, "&:hover": { borderColor: C.ink, backgroundColor: C.paper } },
      outlinedError: { color: C.danger, borderColor: "#E3B9C2", "&:hover": { borderColor: C.danger } },
      text: { color: C.ink, "&:hover": { backgroundColor: "rgba(14,14,14,0.05)" } },
      textError: { color: C.danger },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: R.ctl,
        color: C.ink,
        "&:hover": { backgroundColor: "rgba(14,14,14,0.05)" },
        "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 1 },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: R.box },
      outlined: { borderColor: C.line },
      elevation1: { boxShadow: "none", border: `1px solid ${C.line}` },
    },
  },
  MuiCard: {
    defaultProps: { variant: "outlined" },
    styleOverrides: { root: { borderRadius: R.box, borderColor: C.line, backgroundColor: C.paper, boxShadow: "none" } },
  },
  MuiCardActionArea: { styleOverrides: { root: { "&:hover .MuiCardActionArea-focusHighlight": { opacity: 0.03 } } } },
  MuiTableContainer: { styleOverrides: { root: { borderRadius: R.box } } },
  MuiTable: { styleOverrides: { root: { backgroundColor: C.paper } } },
  MuiTableCell: {
    styleOverrides: {
      root: { borderBottom: `1px solid ${C.line}`, fontSize: "0.84375rem", padding: "11px 12px", color: C.ink },
      head: {
        fontSize: "0.71875rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: C.muted,
        backgroundColor: C.head,
        padding: "9px 12px",
        whiteSpace: "nowrap",
      },
      sizeSmall: { padding: "8px 10px" },
      stickyHeader: { backgroundColor: C.head },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.MuiTableRow-hover:hover": { backgroundColor: C.head },
        "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: C.bone1 },
        "&:last-child > td": { borderBottom: 0 },
      },
    },
  },
  MuiTableSortLabel: { styleOverrides: { root: { "&.Mui-active": { color: C.ink } } } },
  MuiTablePagination: { styleOverrides: { root: { borderTop: `1px solid ${C.line}` } } },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: R.tag, fontWeight: 700, fontSize: "0.75rem", height: 26 },
      sizeSmall: { height: 22, fontSize: "0.71875rem" },
      label: { paddingLeft: 8, paddingRight: 8 },
      outlined: { borderColor: C.line2, backgroundColor: C.paper },
      filled: { backgroundColor: C.bone1, color: C.ink2 },
      colorPrimary: { "&.MuiChip-filled": { backgroundColor: C.ink, color: "#fff" } },
      colorSuccess: { "&.MuiChip-filled": { backgroundColor: "#2E7D4F", color: "#fff" }, "&.MuiChip-outlined": { borderColor: "#2E7D4F", color: "#235F3C" } },
      colorWarning: { "&.MuiChip-filled": { backgroundColor: C.coralText, color: "#fff" }, "&.MuiChip-outlined": { borderColor: C.coralText, color: "#8A2B0C" } },
      colorError: { "&.MuiChip-filled": { backgroundColor: C.danger, color: "#fff" }, "&.MuiChip-outlined": { borderColor: C.danger, color: C.danger } },
      colorInfo: { "&.MuiChip-filled": { backgroundColor: C.moss, color: "#fff" }, "&.MuiChip-outlined": { borderColor: C.moss, color: C.moss } },
      clickable: { "&:hover": { filter: "brightness(0.97)" } },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 44, borderBottom: `1px solid ${C.line}` },
      indicator: { backgroundColor: C.ink, height: 2 },
      scrollButtons: { "&.Mui-disabled": { opacity: 0.3 } },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.875rem",
        minHeight: 44,
        minWidth: 0,
        padding: "10px 2px",
        marginRight: 22,
        color: C.muted,
        "&.Mui-selected": { color: C.ink },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: R.ctl,
        backgroundColor: C.paper,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: C.line2 },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.ink },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: C.ink, borderWidth: 1.5 },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: C.danger },
        "input:-webkit-autofill": { WebkitBoxShadow: "0 0 0 30px #ffffff inset" },
      },
      input: { "&::placeholder": { color: C.faint, opacity: 1 } },
    },
  },
  MuiInputLabel: { styleOverrides: { root: { color: C.muted, "&.Mui-focused": { color: C.ink } } } },
  MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0, marginRight: 0 } } },
  MuiSelect: { styleOverrides: { icon: { color: C.ink } } },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: R.dialog, boxShadow: "0 18px 50px rgba(14,14,14,0.18)", border: `1px solid ${C.line}` },
      paperFullScreen: { borderRadius: 0, border: 0 },
    },
  },
  MuiBackdrop: { styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { backgroundColor: "rgba(14,14,14,0.45)" } } } },
  MuiDialogTitle: {
    styleOverrides: { root: { fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: "1.25rem", letterSpacing: "-0.03em", padding: "18px 20px 8px" } },
  },
  MuiDialogContent: { styleOverrides: { root: { padding: "8px 20px 16px" } } },
  MuiDialogActions: { styleOverrides: { root: { padding: "12px 20px 18px", gap: 8, "& > :not(style) ~ :not(style)": { marginLeft: 0 } } } },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: R.box, alignItems: "center", fontSize: "0.84375rem" },
      standardSuccess: { backgroundColor: C.leafTint, color: C.moss, "& .MuiAlert-icon": { color: C.leaf } },
      standardInfo: { backgroundColor: C.bone1, color: C.ink2, "& .MuiAlert-icon": { color: C.ink2 } },
      standardWarning: { backgroundColor: C.bone, color: "#7A2A0D", "& .MuiAlert-icon": { color: C.coralText } },
      standardError: { backgroundColor: C.dangerTint, color: C.danger, "& .MuiAlert-icon": { color: C.danger } },
      filledSuccess: { backgroundColor: C.moss },
      filledError: { backgroundColor: C.danger },
    },
  },
  MuiSnackbarContent: { styleOverrides: { root: { backgroundColor: C.ink, borderRadius: R.box } } },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { backgroundColor: C.ink, fontSize: "0.75rem", fontWeight: 500, borderRadius: R.ctl, padding: "6px 9px" },
      arrow: { color: C.ink },
    },
  },
  MuiMenu: { styleOverrides: { paper: { border: `1px solid ${C.line}`, boxShadow: "0 12px 32px rgba(14,14,14,0.12)" } } },
  MuiPopover: { styleOverrides: { paper: { border: `1px solid ${C.line}`, boxShadow: "0 12px 32px rgba(14,14,14,0.12)" } } },
  MuiMenuItem: { styleOverrides: { root: { fontSize: "0.875rem", minHeight: 40 } } },
  MuiListItemButton: {
    styleOverrides: { root: { borderRadius: R.ctl, "&.Mui-selected": { backgroundColor: "rgba(14,14,14,0.06)" } } },
  },
  MuiDivider: { styleOverrides: { root: { borderColor: C.line } } },
  MuiSkeleton: { styleOverrides: { root: { backgroundColor: C.line } } },
  MuiLinearProgress: { styleOverrides: { root: { backgroundColor: C.line, borderRadius: 4 }, bar: { backgroundColor: C.ink } } },
  MuiCircularProgress: { defaultProps: { color: "inherit" } },
  MuiSwitch: {
    styleOverrides: {
      switchBase: { "&.Mui-checked": { color: "#fff", "& + .MuiSwitch-track": { backgroundColor: C.ink, opacity: 1 } } },
      track: { backgroundColor: C.line2, opacity: 1 },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 700,
        color: C.muted,
        borderColor: C.line2,
        "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: C.ink, color: "#fff" },
      },
    },
  },
  MuiAvatar: { styleOverrides: { colorDefault: { backgroundColor: C.moss, color: "#fff", fontWeight: 800 } } },
  MuiLink: { styleOverrides: { root: { color: C.ink, fontWeight: 600, textDecorationColor: C.line2, "&:hover": { textDecorationColor: C.ink } } } },
  MuiAccordion: { styleOverrides: { root: { border: `1px solid ${C.line}`, boxShadow: "none", "&:before": { display: "none" } } } },
  MuiTextField: { defaultProps: { variant: "outlined" } },
  MuiBadge: { styleOverrides: { badge: { fontWeight: 700, fontFamily: BODY_FONT } } },
};
